import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Check, Crown, Zap, Shield, Download, Share2, BarChart3, Loader2, XCircle,
  Calendar, GripVertical, MessageSquare, Sparkles, PartyPopper, Trophy, 
  Flame, User, Moon, Target, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { subscriptionAPI } from '../services/api';
import { toast } from 'sonner';

const SubscriptionPage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [trialLoading, setTrialLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statusRes, plansRes] = await Promise.all([
        subscriptionAPI.getStatus(),
        subscriptionAPI.getPlans()
      ]);
      setSubscriptionStatus(statusRes);
      setPlans(plansRes.plans || []);
    } catch (error) {
      console.error('Error loading subscription data:', error);
      toast.error('Failed to load subscription info');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTrial = async () => {
    try {
      setTrialLoading(true);
      const result = await subscriptionAPI.startTrial();
      toast.success(result.message);
      await loadData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to start trial';
      toast.error(message);
    } finally {
      setTrialLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    try {
      setCheckoutLoading(true);
      const result = await subscriptionAPI.createCheckout(planId);
      
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout');
      setCheckoutLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will still have access until the end of your current billing period.')) {
      return;
    }
    
    try {
      setCancelLoading(true);
      const result = await subscriptionAPI.cancelSubscription();
      toast.success(result.message);
      await loadData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to cancel subscription';
      toast.error(message);
    } finally {
      setCancelLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Comprehensive features list organized by category
  const featureCategories = [
    {
      title: 'Habit Tracking',
      features: [
        { icon: Target, text: 'Unlimited habits', color: 'text-blue-500' },
        { icon: Calendar, text: 'Flexible scheduling (daily or specific days)', color: 'text-purple-500' },
        { icon: GripVertical, text: 'Drag & drop to reorder habits', color: 'text-indigo-500' },
        { icon: Flame, text: 'Streak tracking with fire badges', color: 'text-orange-500' },
      ]
    },
    {
      title: 'Engagement & Motivation',
      features: [
        { icon: Sparkles, text: 'AI-powered daily motivational quotes', color: 'text-amber-500' },
        { icon: PartyPopper, text: 'Celebration confetti animations', color: 'text-pink-500' },
        { icon: Trophy, text: 'Progress milestones (25%, 50%, 75%, 100%)', color: 'text-yellow-500' },
        { icon: MessageSquare, text: 'Add notes & journal entries', color: 'text-cyan-500' },
      ]
    },
    {
      title: 'Analytics & Export',
      features: [
        { icon: BarChart3, text: 'Beautiful analytics & charts', color: 'text-green-500' },
        { icon: Download, text: 'Export to CSV & PDF', color: 'text-emerald-500' },
        { icon: Share2, text: 'Share progress on social media', color: 'text-blue-400' },
        { icon: Shield, text: 'Cloud sync across all devices', color: 'text-slate-500' },
      ]
    },
    {
      title: 'Personalization',
      features: [
        { icon: User, text: 'Fun avatar customization (15+ options)', color: 'text-violet-500' },
        { icon: Moon, text: 'Light & dark mode', color: 'text-slate-400' },
        { icon: Crown, text: 'Priority support', color: 'text-purple-500' },
      ]
    }
  ];

  // Flat list for simple display
  const allFeatures = featureCategories.flatMap(cat => cat.features);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="subscription-loading">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-slate-600 dark:text-slate-400">Loading subscription info...</p>
        </div>
      </div>
    );
  }

  // Show active subscription status
  if (subscriptionStatus?.status === 'active') {
    return (
      <div className="max-w-4xl mx-auto space-y-6" data-testid="subscription-active">
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg">
            <Crown className="w-10 h-10 text-white" />
          </div>
          <h2 className="font-heading text-3xl font-bold gradient-text mb-2">Premium Active</h2>
          <p className="text-slate-600 dark:text-slate-400">
            Your subscription is active until {formatDate(subscriptionStatus.subscription_ends_at)}
          </p>
        </div>

        <Card className="gradient-card border-green-200 dark:border-green-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-600 dark:text-green-400">
              <Check className="w-5 h-5" />
              You have full access to all features
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              {featureCategories.map((category, catIndex) => (
                <div key={catIndex}>
                  <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-3">{category.title}</h4>
                  <ul className="space-y-2">
                    {category.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-3 text-slate-600 dark:text-slate-300">
                        <feature.icon className={`w-4 h-4 ${feature.color} flex-shrink-0`} />
                        <span className="text-sm">{feature.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-3 border-t border-slate-200 dark:border-slate-700 pt-6">
            <Button
              variant="outline"
              onClick={handleCancelSubscription}
              disabled={cancelLoading}
              className="w-full border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
              data-testid="cancel-subscription-button"
            >
              {cancelLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Cancel Subscription
                </>
              )}
            </Button>
            <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
              You&apos;ll keep access until your current billing period ends
            </p>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Show trial status
  if (subscriptionStatus?.status === 'trial') {
    const daysRemaining = subscriptionStatus.trial_days_remaining || 0;
    
    return (
      <div className="max-w-4xl mx-auto space-y-6" data-testid="subscription-trial">
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
            <Zap className="w-10 h-10 text-white" />
          </div>
          <h2 className="font-heading text-3xl font-bold gradient-text mb-2">Free Trial Active</h2>
          <p className="text-slate-600 dark:text-slate-400">
            {daysRemaining > 0 ? (
              <>Your trial ends in <span className="font-bold text-purple-600 dark:text-purple-400">{daysRemaining} days</span> ({formatDate(subscriptionStatus.trial_ends_at)})</>
            ) : (
              <>Your trial ends on {formatDate(subscriptionStatus.trial_ends_at)}</>
            )}
          </p>
        </div>

        {/* Trial Banner */}
        <div className="p-6 rounded-2xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
              <Clock className="w-7 h-7 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                Enjoying your trial?
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                Subscribe now to keep all your premium features after the trial ends. 
                <span className="font-semibold text-green-600 dark:text-green-400"> Your data and progress will be saved!</span>
              </p>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <Card className="gradient-card border-blue-200 dark:border-blue-800">
          <CardHeader>
            <CardTitle>All Premium Features Included</CardTitle>
            <CardDescription>
              Everything you need to build and maintain great habits
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              {featureCategories.map((category, catIndex) => (
                <div key={catIndex}>
                  <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                    {category.title}
                  </h4>
                  <ul className="space-y-2">
                    {category.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-3 text-slate-600 dark:text-slate-300">
                        <feature.icon className={`w-4 h-4 ${feature.color} flex-shrink-0`} />
                        <span className="text-sm">{feature.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            {plans.map((plan) => (
              <Button
                key={plan.id}
                onClick={() => handleSubscribe(plan.id)}
                disabled={checkoutLoading}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg text-lg py-6"
                data-testid="subscribe-button"
              >
                {checkoutLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Crown className="w-5 h-5 mr-2" />
                    Subscribe for US${plan.price}/month
                  </>
                )}
              </Button>
            ))}
            <p className="text-sm text-slate-500 dark:text-slate-400 text-center">
              Secure payment powered by Stripe. Cancel anytime.
            </p>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Show subscription plans for new users or expired users
  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="subscription-page">
      <div className="text-center mb-8">
        <h2 className="font-heading text-4xl font-bold gradient-text mb-4">
          Unlock Your Full Potential
        </h2>
        <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
          Start with a <span className="font-semibold text-green-600 dark:text-green-400">free 7-day trial</span>, 
          then continue for just <span className="font-semibold">US$2.99/month</span>. Cancel anytime.
        </p>
      </div>

      {/* Plan Cards */}
      <div className="grid md:grid-cols-2 gap-6 mb-12">
        {/* Free Trial Card */}
        <Card className="gradient-card border-2 border-dashed border-blue-300 dark:border-blue-700 flex flex-col">
          <CardHeader>
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-600/20 flex items-center justify-center mb-4">
              <Zap className="w-7 h-7 text-blue-500" />
            </div>
            <CardTitle className="text-2xl">Start Free Trial</CardTitle>
            <CardDescription>
              7 days free, no credit card required
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1">
            <div className="text-4xl font-bold mb-6">
              $0 <span className="text-lg font-normal text-slate-500">for 7 days</span>
            </div>
            <ul className="space-y-3">
              {allFeatures.slice(0, 6).map((feature, index) => (
                <li key={index} className="flex items-center gap-3 text-slate-600 dark:text-slate-300">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>{feature.text}</span>
                </li>
              ))}
              <li className="flex items-center gap-3 text-slate-400">
                <span className="ml-8 text-sm">+ {allFeatures.length - 6} more features...</span>
              </li>
            </ul>
          </CardContent>
          <CardFooter>
            <Button
              onClick={handleStartTrial}
              disabled={trialLoading}
              variant="outline"
              className="w-full border-blue-300 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 py-6 text-lg"
              data-testid="start-trial-button"
            >
              {trialLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                'Start Free Trial'
              )}
            </Button>
          </CardFooter>
        </Card>

        {/* Premium Card */}
        {plans.map((plan) => (
          <Card 
            key={plan.id} 
            className="gradient-card border-2 border-purple-300 dark:border-purple-700 relative overflow-hidden flex flex-col"
          >
            <div className="absolute top-0 right-0 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs px-4 py-1.5 rounded-bl-xl font-medium">
              BEST VALUE
            </div>
            <CardHeader>
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center mb-4">
                <Crown className="w-7 h-7 text-white" />
              </div>
              <CardTitle className="text-2xl">Premium</CardTitle>
              <CardDescription>
                Full access to all features forever
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1">
              <div className="text-4xl font-bold mb-6">
                US${plan.price} <span className="text-lg font-normal text-slate-500">/month</span>
              </div>
              <ul className="space-y-3">
                {allFeatures.slice(0, 6).map((feature, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-600 dark:text-slate-300">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>{feature.text}</span>
                  </li>
                ))}
                <li className="flex items-center gap-3 text-slate-400">
                  <span className="ml-8 text-sm">+ {allFeatures.length - 6} more features...</span>
                </li>
              </ul>
            </CardContent>
            <CardFooter>
              <Button
                onClick={() => handleSubscribe(plan.id)}
                disabled={checkoutLoading}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg hover-glow py-6 text-lg"
                data-testid="subscribe-button"
              >
                {checkoutLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Subscribe Now'
                )}
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>

      {/* All Features Section */}
      <div className="gradient-card rounded-2xl p-8 border border-white/20 dark:border-slate-700">
        <h3 className="font-heading text-2xl font-bold text-center mb-8 gradient-text">
          Everything You Get
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {featureCategories.map((category, catIndex) => (
            <div key={catIndex}>
              <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-4 pb-2 border-b border-slate-200 dark:border-slate-700">
                {category.title}
              </h4>
              <ul className="space-y-3">
                {category.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3 text-slate-600 dark:text-slate-300">
                    <feature.icon className={`w-5 h-5 ${feature.color} flex-shrink-0 mt-0.5`} />
                    <span className="text-sm">{feature.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <p className="text-center text-sm text-slate-500 dark:text-slate-400">
        Secure payment powered by Stripe. Cancel anytime. No hidden fees.
      </p>
    </div>
  );
};

export default SubscriptionPage;
